import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const DemoDocumentation = () => {
  const navigate = useNavigate();

  const handleDownloadPDF = () => {
    const link = document.createElement('a');
    link.href = '/AI_Fit_Engine_Product_Brief.pdf';
    link.download = 'AI_Fit_Engine_Product_Brief.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sections = [
    {
      number: 1,
      title: "Homepage - Hero Section",
      description: "The landing page features a clean, modern design with a prominent search bar powered by AI. Users can describe their needs in plain English and get matched with relevant AI tools instantly.",
      features: [
        "✨ AI-Powered search with natural language processing",
        "📊 Stats showcase: 150+ AI Tools, 50+ Industries, 10K+ Matches",
        "🎯 Clear value proposition and call-to-action",
        "🔍 Alternative guided assessment option"
      ],
      demoLink: "/"
    },
    {
      number: 2,
      title: "How It Works",
      description: "Three-step process visualization showing users exactly how the platform works, from describing needs to deployment.",
      features: [
        "Step 1: Describe your needs (AI search or guided assessment)",
        "Step 2: Get matched instantly (AI analyzes & recommends)",
        "Step 3: Connect & deploy (direct startup contact)"
      ],
      demoLink: "/"
    },
    {
      number: 3,
      title: "Search Functionality",
      description: "Users can type natural language queries directly into the search bar. The AI understands context and matches based on keywords, industry, and use cases.",
      features: [
        "🤖 Natural language understanding",
        "🔍 Real-time search as you type",
        "🎯 Context-aware matching",
        "⚡ Instant results"
      ],
      demoLink: "/search?q=customer+support"
    },
    {
      number: 4,
      title: "Assessment Flow",
      description: "The guided assessment provides a more detailed matching experience. 10 comprehensive questions that adapt based on industry selection ensure perfect tool matches.",
      features: [
        "📊 Progress bar showing completion percentage",
        "🔄 Adaptive questions based on previous answers",
        "↩️ Back button to revise answers",
        "💼 Industry-specific question paths",
        "✅ 10 comprehensive questions"
      ],
      demoLink: "/assessment"
    },
    {
      number: 5,
      title: "Personalized Recommendations",
      description: "After completing the assessment, users receive a curated list of AI tools perfectly matched to their specific needs, industry, company size, and budget.",
      features: [
        "🎯 Personalized heading based on assessment",
        "📊 Shows industry and parameters used",
        "⭐ Results ranked by rating (best first)",
        "💡 Typically 4-10 highly relevant matches",
        "🔗 Direct links to vendor websites"
      ],
      demoLink: "/assessment"
    },
    {
      number: 6,
      title: "About Page",
      description: "Compelling about page designed to attract both businesses seeking AI solutions and AI startups wanting to be featured on the platform.",
      features: [
        "💡 'We're Not Just a Marketplace. We're Your Growth Partner.'",
        "📊 Platform statistics and impact",
        "🎯 Value for businesses seeking AI",
        "🚀 Unique benefits for AI startups",
        "⚖️ Comparison: Traditional vs. AI Fit Engine"
      ],
      demoLink: "/about"
    },
    {
      number: 7,
      title: "Admin Panel",
      description: "Powerful admin interface for managing all AI tool listings. View all companies at a glance with quick access to edit and delete functions.",
      features: [
        "📋 View all 22 demo AI companies",
        "➕ Add new companies with comprehensive form",
        "✏️ Edit existing companies",
        "🗑️ Delete/deactivate companies",
        "🔍 See company details at a glance",
        "⭐ View ratings and categories"
      ],
      demoLink: "/admin"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-[#1E1E1E] border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <span className="text-xl sm:text-2xl font-bold">Back to Home</span>
          </button>
          <Button
            onClick={handleDownloadPDF}
            className="bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-2"
          >
            <Download size={20} />
            Download PDF
          </Button>
        </div>
      </header>

      {/* Hero */}
      <div className="pt-24 pb-16 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-4">
            🚀 AI Fit Engine Demo
          </h1>
          <p className="text-xl text-orange-100 mb-8">
            Complete Product Walkthrough & Feature Guide
          </p>
          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">22</p>
              <p className="text-sm text-orange-100">Demo Companies</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">17</p>
              <p className="text-sm text-orange-100">Sections</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">100%</p>
              <p className="text-sm text-orange-100">Functional</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        {sections.map((section) => (
          <div key={section.number} className="mb-16 bg-white dark:bg-[#1E1E1E] rounded-xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                {section.number}
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                  {section.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
                  {section.description}
                </p>
              </div>
            </div>

            {/* Features */}
            <div className="bg-gray-50 dark:bg-[#262626] rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-orange-600 dark:text-orange-400 mb-3">
                Key Features:
              </h3>
              <ul className="space-y-2">
                {section.features.map((feature, idx) => (
                  <li key={idx} className="text-gray-700 dark:text-gray-300 flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Try It Live Button */}
            <Button
              onClick={() => {
                sessionStorage.setItem('fromDemo', 'true');
                navigate(section.demoLink);
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Try This Feature Live →
            </Button>
          </div>
        ))}

        {/* Summary */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-6">📊 Complete Platform Summary</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-orange-400 mb-4">Feature Set:</h3>
              <ul className="space-y-2">
                <li>🏠 Homepage with AI-powered search</li>
                <li>📋 10-question adaptive assessment</li>
                <li>🎯 Personalized recommendations</li>
                <li>ℹ️ Compelling about page</li>
                <li>⚙️ Complete admin panel</li>
                <li>🔍 Natural language search</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-orange-400 mb-4">Demo Database:</h3>
              <ul className="space-y-2">
                <li>✅ 22 professional AI companies</li>
                <li>✅ 9 business categories covered</li>
                <li>✅ 11 industries represented</li>
                <li>✅ All company sizes (solo to enterprise)</li>
                <li>✅ Realistic pricing ($49-$999/mo)</li>
                <li>✅ Professional descriptions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoDocumentation;
